import { AssetUrlPipe } from "../../core/pipes/asset-url.pipe";
import { CommonModule } from "@angular/common";
import { Component, inject, AfterViewInit, ViewChild } from "@angular/core";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ofType } from "@ngrx/effects";
import { format } from "date-fns";
import { MessageService } from "primeng/api";
import { Observable } from "rxjs";
import { ZorroTableComponent } from "../../components/common/custom-table/zorro-table.component";
import { ExpandableTableComponent } from "../../components/common/expandable-table/expandable-table.component";
import { LoadingComponent } from "../../components/common/loading/loading.component";
import { AgentCreateModalComponent } from "../../components/modal-templates/agent-create-modal/agent-create-modal.component";
import { AgentFilterModalComponent, AgentFilterState } from "../../components/modal-templates/agent-filter-modal/agent-filter-modal.component";
import { AgentReclaimModalComponent } from "../../components/modal-templates/agent-reclaim-modal/agent-reclaim-modal.component";
import { AgentSetupInfoComponent } from "../../components/modal-templates/agent-setup-info/agent-setup-info.component";
import { DeleteConfirmationComponent } from "../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { LogsModalComponent } from "../../components/modal-templates/logs-modal/logs-modal.component";
import { agentData, agentFields, agentFieldsEdit } from "../../core/dummy/dummy-data";
import { Agent } from "../../core/models/agent-fields";
import { AgentActions } from "../../core/ngrx/actions/agent.actions";
import {
  selectAgent,
  selectAgentError,
  selectAgentLoading,
} from "../../core/ngrx/selectors/agent.selectors";
import { agentTableColumns } from "../../core/table-templates/agent-table";
import { MessageEnum } from "../../core/utils/message.enum";
import { BaseLayoutComponent } from "../base-layout.component";

@Component({
  selector: "app-agent-registry",
  standalone: true,
  imports: [AssetUrlPipe, 
    CommonModule,
    MatIconModule,
    ExpandableTableComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    LoadingComponent,
    ZorroTableComponent
  ],
  templateUrl: "./agent-registry.component.html",
  styleUrl: "./agent-registry.component.scss",
})
export class AgentRegistryComponent extends BaseLayoutComponent implements AfterViewInit {
  @ViewChild('agentTable') tableRef!: ZorroTableComponent;

  private modalService = inject(NgbModal);
  private messageService = inject(MessageService);
  minDate: Date = new Date();

  columns = agentTableColumns;

  /** Full unfiltered agent list — source of truth for filtering. */
  private allAgents: any[] = [];

  /** Currently displayed (filtered) rows. */
  dataSource: any[] = [];

  /** Active filter state persisted across modal opens. */
  activeFilters: AgentFilterState = { types: [], statuses: [] };

  get hasActiveFilters(): boolean {
    return this.activeFilters.types.length > 0 || this.activeFilters.statuses.length > 0;
  }

  // Hide the table's built-in pagination; the header toolbar controls it instead
  tableOptions = { showPagination: false, pageSize: 10 };

  // Pagination state (mirrored from the table for header display)
  currentPage = 1;
  pageSize = 10;

  get paginationTotal(): number { return this.dataSource.length; }
  get paginationStart(): number {
    return this.paginationTotal === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }
  get paginationEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.paginationTotal);
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.tableRef?.onPageIndexChange(this.currentPage);
    }
  }

  nextPage(): void {
    if (this.paginationEnd < this.paginationTotal) {
      this.currentPage++;
      this.tableRef?.onPageIndexChange(this.currentPage);
    }
  }

  onTablePageChange(event: { page: number; size: number }): void {
    this.currentPage = event.page;
    if (event.size) this.pageSize = event.size;
  }
  agentFields = agentFields;
  options = {
    isExpandable: false,
  };
  field: any;

  isLoading: any = false;
  projectId: string = "";
  agent$: Observable<Agent[]> = this.store.select(selectAgent);
  loadingAgents$: Observable<boolean> = this.store.select(selectAgentLoading);
  errorAgents$: Observable<string | null> = this.store.select(selectAgentError);

  editingAgent: any;
  selectedAgentList: any = [];
  lockedAgents: any = [];
  legendData = [
    {
      type: "Online",
      image: "",
    },
    {
      type: "Offline",
      image: "",
    },
    {
      type: "Executing",
      image: "",
    },
    {
      type: "Crashed",
      image: "",
    },
  ];
  intervalId: any;
  isIntervalLoading: boolean = false;
  iconSrc: string = '';
  iconSrcDelete: string = '';

  ngAfterViewInit() {
    this.iconSrc = `${this.basePath}assets/images/agent/reclaim-agent-dark.svg`;
    this.iconSrcDelete = `${this.basePath}assets/images/virtusa-icons/remove-icon-dark.svg`;
  }

  ngOnInit() {
    // Initialize legend images with base path
    this.legendData[0].image = `${this.basePath}assets/images/agent/legend/alive.svg`;
    this.legendData[1].image = `${this.basePath}assets/images/agent/legend/dead.svg`;
    this.legendData[2].image = `${this.basePath}assets/images/agent/legend/lock.svg`;
    this.legendData[3].image = `${this.basePath}assets/images/agent/legend/crash.svg`;

    this.projectId = this.route.snapshot.paramMap.get("id") as string;

    this.store.dispatch(AgentActions.loadAgent({ projectId: this.projectId }));
    this.loadingAgents$.subscribe((loading: any) => {
      this.isLoading = loading;
    });
    //get agents
    this.agent$.subscribe((agents: any) => {
      const toTitleCase = (s: string) =>
        s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
      this.allAgents = (agents ?? []).map((agent: any) => ({
        ...agent,
        statusLabel: toTitleCase(agent.status),
        typeLabel:   toTitleCase(agent.type),
      }));
      this.lockedAgents = this.allAgents.filter(
        (agent: any) => agent.status === "EXECUTING"
      );
      this.applyFilters();
      this.isLoading = false;
    });

    //once reclaim success get agents
    this.actions$
      .pipe(ofType(AgentActions.reclaimAgentSuccess))
      .subscribe(() => {
        console.log("Reclaim successful, reloading agents...");
        this.store.dispatch(
          AgentActions.loadAgent({ projectId: this.projectId })
        );
      });

    this.setIntervalToRefresh();
  }

  setIntervalToRefresh() {
    this.intervalId = setInterval(() => {
      this.isIntervalLoading = true;
      this.loadAgents();
    }, 60000);
  }

  loadAgents() {
    this.store.dispatch(AgentActions.loadAgent({ projectId: this.projectId }));
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  private applyFilters(): void {
    const { types, statuses } = this.activeFilters;
    this.dataSource = this.allAgents.filter((agent: any) => {
      const typeMatch   = types.length    === 0 || types.includes(agent.typeLabel);
      const statusMatch = statuses.length === 0 || statuses.includes(agent.statusLabel);
      return typeMatch && statusMatch;
    });
    // Reset to page 1 whenever filter changes
    this.currentPage = 1;
    this.tableRef?.onPageIndexChange(1);
  }

  openFilterModal(): void {
    const modalRef = this.modalService.open(AgentFilterModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      windowClass: 'agent-filter-modal-window',
    });
    modalRef.componentInstance.activeFilters = {
      types:    [...this.activeFilters.types],
      statuses: [...this.activeFilters.statuses],
    };
    modalRef.result
      .then((result: AgentFilterState) => {
        this.activeFilters = result;
        this.applyFilters();
      })
      .catch(() => { /* dismissed — no change */ });
  }

  handleActionsInTable(event: any) {
    if (event.type == "svg") {
      this.openGenericModal(true, event.data?.row);
    } else if (event.type == "copy") {
      this.copyToClipboard(event.data?.row?.apiKey);
    } else if (event.type == "log-svg" || event.type == "conditional-svg") {
      this.openLogs(event.data?.row);
    } else if (event.data?.column?.key === 'setup') {
      this.setupAgentModal(event.data?.row.apiKey);
    }
  }

  setupAgentModal(key: string) {
    const modalRef = this.modalService.open(AgentSetupInfoComponent, {
      centered: true,
      size: "lg",
      backdrop: 'static',
    });
    modalRef.componentInstance.key = key;
    modalRef.componentInstance.projectId = this.projectId;
    modalRef.result.then((result) => {
      console.log(result);
    });
  }

  openLogs(data: any) {
    let configs = {
      title: "Logs",
      data: data?.crashLogs ? data?.crashLogs : '',
    };
    const modalRef = this.modalService.open(LogsModalComponent, {
      centered: true,
      size: "xl",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = configs;
    modalRef.result.then((result) => {
      console.log(result);
    });
  }
  
  copyToClipboard(value: any): void {
    // Use Clipboard API to copy text
    navigator.clipboard
      .writeText(value)
      .then(() => {
        this.toaster.pop({ type: 'success', text: MessageEnum.AGENT_TOKEN_COPY_SUCCESS }, { backgroundColor: 'transparent' });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  reclaimAgent() {
    let data = {
      displayName: "Reclaim Execution Engine",
      saveBtnText: "Reclaim",
    };
    const modalRef = this.modalService.open(AgentReclaimModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      size: "lg",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      title: data.displayName,
      saveBtnText: data.saveBtnText,
    };
    modalRef.componentInstance.agents = this.dataSource;

    modalRef.result
      .then(
        (result) => {
          console.log(result);
          let idList = Object.values(result?.value).map((item: any) => item.id);
          this.store.dispatch(
            AgentActions.reclaimAgent({
              projectId: this.projectId,
              agentIdList: idList,
            })
          );
        });
  }

  openDeleteConfirmationModal() {
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });

    modalRef.result.then((result) => {
      if (result) {
        this.removeAgent();
      }
    });
  }

  removeAgent() {
    let agentIdList = Object.values(this.selectedAgentList)
      .filter((item: any) => item.status !== "EXECUTING")
      .map((item: any) => item.id);

    if (agentIdList.length > 0) {
      this.store.dispatch(
        AgentActions.deleteAgent({
          projectId: this.projectId,
          agentIdList: agentIdList
        })
      );
      this.selectedAgentList = [];
    } else {
      this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_EXECUTING_DELETE_FAILURE }, { backgroundColor: 'transparent' });
    }
  }

  openGenericModal(isEdit: boolean, rowData?: any) {
    this.editingAgent = rowData;
    let data = {
      displayName: isEdit ? "Update execution engine" : "Create new execution engine",
      saveBtnText: isEdit ? "Update" : "Create",
    };

    const modalRef = this.modalService.open(AgentCreateModalComponent, {
      centered: true,
      size:"lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      title: data.displayName,
      saveBtnText: data.saveBtnText,
      isEdit: isEdit,
      model: rowData,
    };
    modalRef.componentInstance.fields = isEdit ? agentFieldsEdit : agentFields;

    modalRef.result.then((result) => {
      // result
      let data = {
        type: "LOCAL",
        name: "AgentNew",
        expiration: "2025-02-14",
      };
      const formattedData = {
        ...result.value,
        expiration: format(
          new Date(result.value.expiration),
          "yyyy-MM-dd'T'HH:mm:ss.SSSSSS"
        ),
      };

      if (isEdit) {
        this.editingAgent = {
          ...this.editingAgent,
          name: rowData.name,
          expiration: formattedData.expiration,
          type: formattedData.type,
        };

        this.store.dispatch(
          AgentActions.updateAgent({
            projectId: this.projectId,
            agent: this.editingAgent,
          })
        );
      } else {
        this.store.dispatch(
          AgentActions.createAgent({
            projectId: this.projectId,
            // data: data,
            data: formattedData,
          })
        );
      }
    });
  }

  handleRowSelect(event: any) {
    this.selectedAgentList = event?.data;
    console.log("selected agents ", this.selectedAgentList);
  }

  changeIcon(isActive: boolean) {
    this.iconSrc = isActive
      ? `${this.basePath}assets/images/agent/reclaim-agent-light.svg`
      : `${this.basePath}assets/images/agent/reclaim-agent-dark.svg`;
  }

  changeRemoveIcon(isActive: boolean) {
    this.iconSrcDelete = isActive
      ? `${this.basePath}assets/images/virtusa-icons/remove-icon-light.svg`
      : `${this.basePath}assets/images/virtusa-icons/remove-icon-dark.svg`;
  }
}
